import { addDays } from "@prismicio/editor-support/Date"
import { Box, Skeleton, Text } from "@prismicio/editor-ui"
import type { PropsWithChildren, ReactNode } from "react"

import type {
  BillingPeriod,
  QuotaOrLimit,
  Subscription,
} from "../../app/services/billing"
import type { Usage } from "../../app/services/usage"
import { useSubscriptionQuery } from "../billing/useSubscription"
import { UsageDetailsCard, UsageDetailsCardSkeleton } from "./UsageDetailsCard"
import { UsageProgressBar, UsageProgressBarSkeleton } from "./UsageProgressBar"
import { formatValue, type Unit } from "./usageUtils"
import { useUsageQuery } from "./useUsage"

interface UsageSummaryItemProps extends UsageSummaryItemSkeletonProps {
  selectedBillingPeriod: BillingPeriod
}

export function UsageSummaryItem(props: UsageSummaryItemProps) {
  const { itemTypeKey, selectedBillingPeriod, ...otherProps } = props

  const itemType = usageSummaryItemTypes[itemTypeKey]

  const { data: subscription } = useSubscriptionQuery()
  const { data: usage } = useUsageQuery(
    selectedBillingPeriod.startDate,
    addDays(selectedBillingPeriod.endDate, 1),
  )

  const quota = itemType.getQuota(subscription)
  const limit = itemType.getLimit(subscription)

  const description = getDescription({ limit, quota, unit: itemType.unit })

  const totalUsage = itemType.getTotalUsage(usage)

  let usageProgressBar, usageDetailsCard
  if (limit === "unlimited" || quota === "unlimited") {
    usageDetailsCard = (
      <UsageDetailsCard usage={{ used: totalUsage }} unit={itemType.unit} />
    )
  } else {
    usageProgressBar = (
      <UsageProgressBar quota={quota} limit={limit} totalUsage={totalUsage} />
    )

    const quotaUsage = Math.min(quota, totalUsage)
    const overageUsage = totalUsage - quotaUsage

    usageDetailsCard = (
      <UsageDetailsCard
        usage={{ used: quotaUsage, included: quota }}
        overage={{ used: overageUsage, included: limit - quota }}
        unit={itemType.unit}
      />
    )
  }

  return (
    <UsageSummaryItemTemplate
      {...otherProps}
      title={itemType.name}
      description={<Text color="grey11">{description}</Text>}
    >
      {usageProgressBar}
      {usageDetailsCard}
    </UsageSummaryItemTemplate>
  )
}

type UsageSummaryItemTemplateProps = PropsWithChildren<{
  title: ReactNode
  description: ReactNode
  billingPeriodSelect?: ReactNode
}>

function UsageSummaryItemTemplate(props: UsageSummaryItemTemplateProps) {
  const { title, description, billingPeriodSelect, children } = props
  return (
    <Box flexDirection="column" gap={16}>
      <Box gap={8}>
        <Box flexDirection="column" flexGrow={1} gap={4}>
          <Text component="h4" variant="bold">
            {title}
          </Text>
          {description}
        </Box>
        {billingPeriodSelect}
      </Box>
      {children}
    </Box>
  )
}

interface UsageSummaryItemType {
  name: string
  unit: Unit
  getQuota: (subscription: Subscription) => QuotaOrLimit
  getLimit: (subscription: Subscription) => QuotaOrLimit
  getTotalUsage: (usage: Usage) => number
}

export const usageSummaryItemTypes = {
  "cdn-bandwidth": {
    name: "CDN bandwidth",
    unit: "gigabyte",
    getQuota: (subscription) => subscription.quotas.bandwidth,
    getLimit: (subscription) => subscription.limits.bandwidth,
    getTotalUsage: (usage) =>
      Number(usage.totalCloudFrontBandwidth + usage.totalImgixBandwidth) /
      1_000_000_000,
  },
  "api-calls": {
    name: "API calls",
    unit: undefined,
    getQuota: (subscription) => subscription.quotas.apiCalls,
    getLimit: (subscription) => subscription.limits.apiCalls,
    getTotalUsage: (usage) =>
      Number(usage.totalCloudFrontCalls + usage.totalImgixCalls),
  },
} satisfies Record<string, UsageSummaryItemType>

interface GetDescriptionArgs {
  limit: QuotaOrLimit
  quota: QuotaOrLimit
  unit: Unit
}

function getDescription(args: GetDescriptionArgs) {
  const { limit, quota, unit } = args

  if (limit === "unlimited" || quota === "unlimited") {
    return "Plan with custom limits"
  }

  const humanReadableQuota = formatValue(quota, { unit })

  if (limit === quota) return `${humanReadableQuota} included`

  const humanReadableLimit = formatValue(limit, { unit })

  return `${humanReadableQuota} included (paid overages allowed up to ${humanReadableLimit})`
}

export interface UsageSummaryItemSkeletonProps
  extends Pick<UsageSummaryItemTemplateProps, "billingPeriodSelect"> {
  itemTypeKey: keyof typeof usageSummaryItemTypes
}

export function UsageSummaryItemSkeleton(props: UsageSummaryItemSkeletonProps) {
  const { itemTypeKey, ...otherProps } = props

  return (
    <UsageSummaryItemTemplate
      {...otherProps}
      title={usageSummaryItemTypes[itemTypeKey].name}
      description={<Skeleton width={352} height={24} />}
    >
      <UsageProgressBarSkeleton />
      <UsageDetailsCardSkeleton />
    </UsageSummaryItemTemplate>
  )
}
